import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CreditCard, Save, Link2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function AdminPaiement() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", {
        stripe_enabled: settings.stripe_enabled,
        stripe_link_annual: settings.stripe_link_annual,
        stripe_link_monthly: settings.stripe_link_monthly,
        annual_price: parseFloat(settings.annual_price || 0),
        monthly_price: parseFloat(settings.monthly_price || 0),
        auto_activation_enabled: settings.auto_activation_enabled,
      });
      setSettings(data);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="p-10 text-sm">Chargement…</div>;

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Paiement" title="Configuration Stripe" sub="Activez le paiement en ligne et renseignez vos liens Stripe Payment Links (annuel et/ou mensuel)." />
      <div className="p-6 lg:p-10 max-w-3xl space-y-6">
        {/* Activation */}
        <Card icon={CreditCard} title="Activation du paiement en ligne">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="text-sm font-semibold mb-1">Paiement Stripe activé</div>
              <p className="text-xs text-[#495057]">Lorsque activé, les visiteurs du site et les chefs d'entreprise voient un bouton "Payer en ligne".</p>
            </div>
            <Switch
              checked={!!settings.stripe_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, stripe_enabled: v })}
              data-testid="stripe-enabled-toggle"
            />
          </div>
          <div className="flex items-start justify-between gap-6 pt-4 border-t border-[#DEE2E6]">
            <div className="flex-1">
              <div className="text-sm font-semibold mb-1">Activation automatique après paiement</div>
              <p className="text-xs text-[#495057]">
                Si activé, l'entreprise est automatiquement activée pour 1 mois / 12 mois dès le retour Stripe.
                Sinon, le paiement est enregistré en attente et vous devez le valider manuellement.
              </p>
            </div>
            <Switch
              checked={!!settings.auto_activation_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, auto_activation_enabled: v })}
              data-testid="auto-activation-toggle"
            />
          </div>
        </Card>

        {/* Liens */}
        <Card icon={Link2} title="Stripe Payment Links">
          <div className="text-xs text-[#495057] mb-4">
            Créez vos Payment Links dans votre dashboard Stripe (<a href="https://dashboard.stripe.com/payment-links" target="_blank" rel="noreferrer" className="text-[#002FA7] font-semibold">dashboard.stripe.com</a>),
            configurez la redirection après paiement vers <code className="font-mono bg-[#F8F9FA] px-1">{window.location.origin}/payment/success</code>,
            puis collez les URLs ci-dessous.
          </div>

          <div className="space-y-4">
            <div className="p-4 border border-[#DEE2E6]">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.2em] font-bold">Abonnement annuel</div>
                <div className="font-display text-2xl font-black tracking-tight">
                  <Input
                    type="number" step="1"
                    value={settings.annual_price || ""}
                    onChange={(e) => setSettings({ ...settings, annual_price: e.target.value })}
                    data-testid="annual-price"
                    className="w-32 h-9 inline-block text-right font-display font-black text-lg"
                  /> €/an
                </div>
              </div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Lien Stripe annuel</Label>
              <Input
                type="url"
                value={settings.stripe_link_annual || ""}
                onChange={(e) => setSettings({ ...settings, stripe_link_annual: e.target.value })}
                placeholder="https://buy.stripe.com/..."
                data-testid="stripe-link-annual"
                className="h-11 font-mono text-xs mt-1"
              />
            </div>

            <div className="p-4 border border-[#DEE2E6]">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.2em] font-bold">Abonnement mensuel (optionnel)</div>
                <div className="font-display text-2xl font-black tracking-tight">
                  <Input
                    type="number" step="1"
                    value={settings.monthly_price || ""}
                    onChange={(e) => setSettings({ ...settings, monthly_price: e.target.value })}
                    data-testid="monthly-price"
                    className="w-32 h-9 inline-block text-right font-display font-black text-lg"
                  /> €/mois
                </div>
              </div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Lien Stripe mensuel</Label>
              <Input
                type="url"
                value={settings.stripe_link_monthly || ""}
                onChange={(e) => setSettings({ ...settings, stripe_link_monthly: e.target.value })}
                placeholder="https://buy.stripe.com/..."
                data-testid="stripe-link-monthly"
                className="h-11 font-mono text-xs mt-1"
              />
            </div>
          </div>
        </Card>

        {/* Info box */}
        {settings.stripe_enabled ? (
          <div className="p-5 border border-[#059669] bg-[#ECFDF5] text-sm flex items-start gap-3" data-testid="stripe-status-on">
            <CheckCircle2 className="h-5 w-5 text-[#059669] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-display font-bold text-[#065F46] mb-1">Paiement en ligne actif</div>
              <p className="text-[#065F46]">Les visiteurs du Landing et les chefs d'entreprise peuvent payer via Stripe.</p>
            </div>
          </div>
        ) : (
          <div className="p-5 border border-[#FCD34D] bg-[#FFFBEB] text-sm flex items-start gap-3" data-testid="stripe-status-off">
            <AlertTriangle className="h-5 w-5 text-[#92400E] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-display font-bold text-[#92400E] mb-1">Paiement en ligne inactif</div>
              <p className="text-[#92400E]">Les souscriptions se font uniquement par email / téléphone. Activez Stripe pour permettre le paiement instantané.</p>
            </div>
          </div>
        )}

        <div className="sticky bottom-0 bg-white p-4 border border-[#0A0A0A] flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="settings-save" className="bg-[#002FA7] hover:bg-[#002175] text-white font-semibold h-11 px-6">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer les paramètres"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-[#DEE2E6] p-6">
      <div className="flex items-center gap-3 mb-5">
        <Icon className="h-5 w-5 text-[#002FA7]" />
        <h3 className="font-display font-bold text-lg tracking-tight">{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
